import { useEffect, useRef, useState, useCallback } from 'react'
import { type IDropdownItem } from 'shared/ui/Dropdown'

export const useDynamicVisibleChips = (
  containerRef: React.RefObject<HTMLElement>,
  placeholderWrapRef: React.RefObject<HTMLDivElement>,
  selectedItems: IDropdownItem[] | undefined,
  maxVisibleChips?: number,
  gap = 8
) => {
  const [visibleCount, setVisibleCount] = useState<number | undefined>(undefined)
  const chipRefs = useRef<Map<string | number, HTMLDivElement>>(new Map())
  const remainderCountRef = useRef<HTMLDivElement | null>(null)
  const prevVisibleCountRef = useRef<number | undefined>(undefined)

  const calculateVisible = useCallback(() => {
    const wrap = placeholderWrapRef.current
    if (!wrap || !selectedItems?.length || maxVisibleChips === undefined) {
      return
    }

    const allElementsRendered = selectedItems.every((item) => {
      const element = chipRefs.current.get(item.id)
      return element && element.offsetWidth > 0
    })

    if (!allElementsRendered) {
      return
    }
    const containerWidth = wrap.offsetWidth
    if (containerWidth <= 0) return

    const counterWidth = (remainderCountRef.current?.offsetWidth || 40) + gap
    let currentWidth = 0
    let count = 0
    const checkCount = Math.min(maxVisibleChips, selectedItems.length)

    for (let i = 0; i < checkCount; i++) {
      const item = selectedItems[i]
      const element = chipRefs.current.get(item.id)
      if (!element) break

      const itemWidth = element.offsetWidth + gap
      const isLastItem = i === selectedItems.length - 1
      const spaceNeeded = isLastItem
        ? currentWidth + itemWidth
        : currentWidth + itemWidth + counterWidth

      if (spaceNeeded <= containerWidth) {
        currentWidth += itemWidth
        count++
      } else {
        break
      }
    }

    setVisibleCount(count)
    prevVisibleCountRef.current = count
  }, [placeholderWrapRef, selectedItems, maxVisibleChips])

  useEffect(() => {
    if (maxVisibleChips === undefined) {
      setVisibleCount(undefined)
      chipRefs.current.clear()
      return
    }

    if (!selectedItems?.length) {
      setVisibleCount(0)
      chipRefs.current.clear()
      prevVisibleCountRef.current = 0
      return
    }

    setVisibleCount(undefined)

    const resizeObserver = new ResizeObserver(() => {
      calculateVisible()
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    if (placeholderWrapRef.current) {
      resizeObserver.observe(placeholderWrapRef.current)
    }

    calculateVisible()

    return () => {
      resizeObserver.disconnect()
    }
  }, [calculateVisible, selectedItems, maxVisibleChips, containerRef, placeholderWrapRef])

  const currentLength = selectedItems?.length ?? 0
  const maxAllowed = maxVisibleChips ? Math.min(maxVisibleChips, currentLength) : currentLength
  const prevValue = prevVisibleCountRef?.current || maxAllowed

  return {
    visibleCount: visibleCount ?? Math.min(prevValue, currentLength),
    hiddenCount:
      selectedItems && visibleCount !== undefined
        ? Math.max(0, selectedItems.length - visibleCount)
        : 0,
    chipRefs,
    remainderCountRef,
  }
}
